import React from 'react';
import { TherapeuticArea } from '../../services/api/domains/therapeutic-areas/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  therapeuticAreaFields,
  therapeuticAreaIdField,
  therapeuticAreaAlwaysDisabledFields,
} from './configs/therapeuticAreaFields';

interface TherapeuticAreaModalProps {
  therapeuticArea: TherapeuticArea;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedTherapeuticArea: TherapeuticArea) => void;
  close: () => void;
}

const TherapeuticAreaModal: React.FC<TherapeuticAreaModalProps> = ({
  therapeuticArea,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedTherapeuticArea,
    setEditedEntity: setEditedTherapeuticArea,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: therapeuticArea,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: therapeuticAreaAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'EDIT'
              ? 'Edit Therapeutic Area'
              : 'Therapeutic Area Details'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {/* Therapeutic Area ID field if present */}
          {therapeuticArea.therapeutic_area_id && (
            <ModalFieldRenderer
              fields={[therapeuticAreaIdField]}
              entity={editedTherapeuticArea}
              setEntity={setEditedTherapeuticArea}
              mode={modalMode}
              focusedField={focusedField}
              setFocusedField={setFocusedField}
              isFieldDisabled={isFieldDisabled}
              getFloatingLabelStyle={getFloatingLabelStyle}
              getFloatingInputStyle={getFloatingInputStyle}
              styles={styles}
              missingMessage="Unknown"
            />
          )}

          {/* All therapeutic area fields */}
          <ModalFieldRenderer
            fields={therapeuticAreaFields}
            entity={editedTherapeuticArea}
            setEntity={setEditedTherapeuticArea}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TherapeuticAreaModal);
export type { ModalMode };
